import { BellAlertIcon, CheckBadgeIcon } from '@heroicons/react/24/outline';

import Footer from '@/renderer/Footer';
import Header from '@/renderer/Header';

import { type Phase, orderPhases } from './orderPhases';

export default ReleaseSchedulePage;

// Small sport chip that labels which sport a card covers.
function SportChip({ label }: { label: string }) {
  return (
    <span className="inline-flex items-center rounded-md bg-emerald-600/10 px-2 py-0.5 text-xs font-bold uppercase tracking-wide text-emerald-700 dark:bg-emerald-400/10 dark:text-emerald-400">
      {label}
    </span>
  );
}

// A labeled row inside a phase card: label on the left, text on the right,
// stacking vertically on mobile.
function InfoRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-1 md:flex-row md:gap-4">
      <dt className="shrink-0 font-semibold text-zinc-500 md:w-36 dark:text-zinc-400">{label}</dt>
      <dd className="m-0 text-zinc-700 dark:text-zinc-300">{children}</dd>
    </div>
  );
}

// One card per phase: sport chip + phase name, then the labeled rows.
function PhaseCard({
  sport,
  phase,
  inSeason = false,
  children,
}: {
  sport: string;
  phase: string;
  inSeason?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-xl border border-emerald-600/20 bg-white p-6 shadow-sm dark:border-emerald-400/20 dark:bg-zinc-900/40">
      <div className="flex flex-wrap items-center gap-2">
        <SportChip label={sport} />
        <h2 className="text-lg font-bold text-zinc-900 dark:text-zinc-100">{phase}</h2>
        {inSeason && (
          <span className="ml-auto rounded-full bg-emerald-600 px-2 py-0.5 font-semibold text-[11px] text-white">
            In season
          </span>
        )}
      </div>
      <dl className="mt-4 space-y-3 text-sm leading-6">{children}</dl>
    </div>
  );
}

const PHASES: Phase[] = [
  {
    key: 'nfl-preseason',
    sport: 'NFL',
    phase: 'Preseason',
    months: [7],
    body: (
      <>
        <InfoRow label="First update">
          Mondays, as soon as the market is ready. Timing swings week to week (as early as 8:00 AM
          PT, sometimes as late as 5:00 PM PT).
        </InfoRow>
        <InfoRow label="Through the week">
          Additional updates each morning, especially day of game when first-half markets open.
        </InfoRow>
      </>
    ),
  },
  {
    key: 'nfl-regular',
    sport: 'NFL',
    phase: 'Regular season',
    months: [8, 9, 10, 11, 0, 1],
    body: (
      <>
        <InfoRow label="First update">Mondays, as early as 8:00 AM PT.</InfoRow>
        <InfoRow label="Through the week">
          Occasional additional updates (less often than preseason). Possible first-half plays later
          in the week.
        </InfoRow>
      </>
    ),
  },
  {
    key: 'cfb',
    sport: 'CFB',
    phase: 'College football',
    months: [7, 8, 9, 10, 11, 0],
    body: (
      <>
        <InfoRow label="FBS sides">
          Most will be released Monday morning around 8-10am PT, will tighten that window a lot
          after Week 2 lines are posted. Possibility for some additional sides throughout the week
          but not often.
        </InfoRow>
        <InfoRow label="FBS totals">
          Most will be released Tuesday morning/early afternoon around 9-11am PT, again we will
          tighten that window a lot after seeing how the market looks for Week 2. Possibility for
          some additional totals throughout the week but not often.
        </InfoRow>
        <InfoRow label="FCS sides & totals">
          Always on morning of game, typically 7-9am PT or as soon as market looks ready.
        </InfoRow>
        <InfoRow label="2nd halves">Possible at any halftime of a FBS game.</InfoRow>
      </>
    ),
  },
];

export function ReleaseSchedule() {
  return (
    <div className="flex h-40 min-h-[stretch] flex-1 grow flex-col justify-between overflow-hidden">
      <Header title="Release schedule" className="py-2 md:hidden" />
      <div className="overflow-auto">
        <div className="mx-auto max-w-2xl px-4 pb-24 pt-12 sm:px-6 sm:pt-20 lg:px-8">
          {/* ── Hero ── */}
          <div className="text-center">
            <h1 className="text-3xl font-bold tracking-tight text-zinc-900 sm:text-4xl dark:text-zinc-100">
              Release schedule
            </h1>
            <p className="mt-4 text-base leading-7 text-zinc-600 dark:text-zinc-400">
              Last updated on Aug. 19, 2026
            </p>
          </div>

          {/* ── Market-ready definition callout ── */}
          <div className="mt-10 rounded-xl border border-emerald-600/20 bg-emerald-50 p-5 dark:border-emerald-400/20 dark:bg-emerald-500/10">
            <div className="flex gap-3">
              <CheckBadgeIcon className="size-6 shrink-0 text-emerald-600 dark:text-emerald-400" />
              <div className="text-sm leading-6 text-zinc-700 dark:text-zinc-200">
                <p className="font-semibold text-zinc-900 dark:text-zinc-100">
                  When the market is &lsquo;ready.&rsquo;
                </p>
                <p className="mt-1">
                  We release once a combination of prominent offshore, regulated, and PPH books have
                  posted lines. Windows are market dependent, so times move.
                </p>
              </div>
            </div>
          </div>

          {/* ── Phase cards, in-season first ── */}
          <div className="mt-8 space-y-6">
            {orderPhases(PHASES, new Date()).map(({ phase, inSeason }) => (
              <PhaseCard
                key={phase.key}
                sport={phase.sport}
                phase={phase.phase}
                inSeason={inSeason}
              >
                {phase.body}
              </PhaseCard>
            ))}
          </div>

          {/* ── Footer note ── */}
          <div className="mt-10 flex items-center justify-center gap-2 text-center text-sm text-zinc-500 dark:text-zinc-400">
            <BellAlertIcon className="size-5 shrink-0" />
            <span>
              You&apos;ll always get a 10+ minute warning before any release, in the Release Center.
            </span>
          </div>
        </div>
        <Footer />
      </div>
    </div>
  );
}

// Main component
function ReleaseSchedulePage() {
  return <ReleaseSchedule />;
}
